import { useEffect, useRef, type ReactNode } from "react";
import type { BaseSpec } from "@howcomet/shared";
import { BigButton } from "../components/BigButton";
import { FigureView } from "../figures/registry";

/**
 * The chrome every shell sits in, and the reason no shell can dead-end. It owns
 * the three outcomes CLAUDE.md requires: a wrong answer that is never punitive,
 * a retry that is always available, and a completion that always ends with the
 * star.
 *
 * A shell renders its interaction and reports state up. It never renders its own
 * feedback, retry button, or completion — so a new shell cannot forget one.
 */
export interface CheckFrameProps {
  /**
   * The whole spec, not the three fields off it.
   *
   * A shell used to pass prompt, right and wrong separately, which meant adding
   * `figure` would have been a fourth thing four shells had to remember and a
   * fifth shell could omit. Taking the spec is what actually makes the
   * guarantee — the frame reads what it needs, and a shell physically cannot
   * drop a field it never touches.
   */
  spec: BaseSpec;
  children: ReactNode;
  /** Null while the child is still working. */
  outcome: "right" | "wrong" | null;
  /** Provided when a retry is possible. Absent once they've got it. */
  onRetry?: () => void;
  onCollect: () => void;
  collecting: boolean;
}

export function CheckFrame({
  spec,
  children,
  outcome,
  onRetry,
  onCollect,
  collecting,
}: CheckFrameProps) {
  const outcomeRef = useRef<HTMLDivElement>(null);

  /*
    Bring the verdict — and the star waiting under it — onto the screen.

    A sort or a match with five items is taller than an iPad's visible area once
    the keyboard has been up, so a child answered the last one and the result
    appeared below the fold. Nothing looked broken; the check simply seemed to
    stop, with the collect button somewhere they had no reason to look. On a
    laptop this is invisible, which is why it took a real device to find.

    Anchored to the BOTTOM of the whole check, because that is where both the
    verdict and the star sit — the collect button is a sibling below the panel,
    so scrolling the feedback alone into view would still leave it off screen on
    a tall sort. `clears-bottom-bars` keeps it out from under the composer and
    the tab bar, both of which are fixed over the bottom of the screen.

    Instant, not smooth, for the reason recorded in SkyScreen: `behavior:
    "smooth"` was seen silently doing nothing, and a scroll that might not
    happen is worse than one without a flourish.
  */
  useEffect(() => {
    if (!outcome) return;
    const frame = requestAnimationFrame(() =>
      outcomeRef.current?.scrollIntoView({ block: "end" }),
    );
    return () => cancelAnimationFrame(frame);
  }, [outcome]);

  return (
    <div ref={outcomeRef} className="mt-5 pop clears-bottom-bars">
      <div className="rounded-3xl border-2 border-lilac bg-panel2 p-5">
        <p className="disp mb-4 text-[21px] leading-snug text-cream">{spec.prompt}</p>

        <FigureView figure={spec.figure} />

        {children}

        {outcome && (
          <p
            className={`pop mt-4 text-[17px] font-bold ${
              outcome === "right" ? "text-mint" : "text-amber"
            }`}
            role="status"
            aria-live="polite"
          >
            {outcome === "right" ? "🎉 " : "🤔 "}
            {outcome === "right" ? spec.right : spec.wrong}
          </p>
        )}

        {/* A miss is never the end. The correction has already given the answer
            away above; this is just the way back in. */}
        {outcome === "wrong" && onRetry && (
          <button
            onClick={onRetry}
            className="kidbtn disp mt-4 rounded-full bg-gold px-5 py-2 text-night"
          >
            Try again
          </button>
        )}
      </div>

      {outcome === "right" && (
        <div className="mt-4">
          <BigButton onClick={onCollect} disabled={collecting}>
            {collecting ? "Collecting…" : "Collect my star ⭐"}
          </BigButton>
        </div>
      )}
    </div>
  );
}
